import type { UserManager } from 'oidc-client-ts';
import { ApiError } from '../api/client';

export class AuthTransportError extends ApiError {
  readonly stage = 'token';

  constructor(status: number, contentType: 'text/html' | 'application/xhtml+xml') {
    super('The sign-in service returned an unavailable gateway response.', status, undefined, undefined, contentType);
    this.name = 'AuthTransportError';
  }
}

type TokenTransport = {
  fetchWithTimeout: (...args: unknown[]) => Promise<Response>;
};
type OidcInternals = {
  _client?: { _tokenClient?: { _jsonService?: TokenTransport } };
};

/**
 * oidc-client-ts 3.4.1 throws a plain Error for HTML before preserving HTTP
 * status. It has no public fetch option, so guard the existing instance's
 * token transport at the response boundary. Do not inspect requests, URLs,
 * headers containing credentials, or bodies, and leave OIDC JSON/nonce handling
 * to the library. Installed-library unit and browser fixtures cover this hook.
 */
export function preserveOidcGatewayStatus(manager: UserManager): void {
  const transport = (manager as unknown as OidcInternals)._client?._tokenClient?._jsonService;
  if (!transport || typeof transport.fetchWithTimeout !== 'function') {
    throw new Error('The sign-in token transport is unavailable.');
  }
  const fetchWithTimeout = transport.fetchWithTimeout;
  transport.fetchWithTimeout = async (...args) => {
    const response = await fetchWithTimeout.apply(transport, args);
    const contentType = response.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase();
    if ((response.status === 408 || response.status === 429 || response.status >= 500) &&
        (contentType === 'text/html' || contentType === 'application/xhtml+xml')) {
      throw new AuthTransportError(response.status, contentType);
    }
    return response;
  };
}
